import {
  useChatConfig,
  useChats,
  useCreateChat,
  useDeleteChat,
  useMessages,
  useRefinePromptApi,
  useScoreAnswerApi,
  useUpdateChatApi,
} from '@/api/chat';
import { CreateChatRequest } from '@ditto/shared';
import { useMemo } from 'react';

export const useChatsService = (
  page?: number,
  limit?: number,
  searchString?: string
) => {
  const { data, ...rest } = useChats(page, limit, searchString);

  const chats = useMemo(() => {
    return data?.data ?? [];
  }, [data]);

  const pagination = data?.pagination;

  return { chats, pagination, ...rest };
};

export const useMessagesService = (chatId: string) => {
  const { data, ...rest } = useMessages(chatId);

  const messages = useMemo(() => {
    return data?.data ?? [];
  }, [data]);

  return { messages, ...rest };
};

export const useDeleteChatService = (chatId: string) => {
  const { mutate: deleteChat, ...rest } = useDeleteChat(chatId);

  return { deleteChat, ...rest };
};

export const useChatConfigService = (chatId: string) => {
  const { data, ...rest } = useChatConfig(chatId);

  return { chatConfig: data, ...rest };
};

export const useUpdateChatService = () => {
  const {
    mutate: updateChat,
    isPending: isUpdatingChat,
    ...rest
  } = useUpdateChatApi();

  return { updateChat, isUpdatingChat, ...rest };
};

export const useRefinePromptService = () => {
  const {
    mutate: refinePrompt,
    isPending: isRefining,
    ...rest
  } = useRefinePromptApi();

  return { refinePrompt, isRefining, ...rest };
};

export const useScoreAnswerService = () => {
  const { mutate: scoreAnswer, ...rest } = useScoreAnswerApi();

  return { scoreAnswer, ...rest };
};

export const useCreateChatService = () => {
  const createChatMutation = useCreateChat();

  const createChat = async (data: CreateChatRequest) => {
    return await createChatMutation.mutateAsync(data);
  };

  return {
    createChat,
    isCreating: createChatMutation.isPending,
    error: createChatMutation.error,
  };
};
